import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AttributeValueModel, ProductDto } from "@insite/client-framework/Types/ApiModels";
import {
    AttributeValueByProduct,
    batchGetProductsByCustomVariant,
    BatchGetProductsByCustomVariantApiParameter,
    ProductModelByVariant,
} from "@thos/Services/ProductServiceExtended";

type Props = {
    apiResult?: ProductModelByVariant | null;
    products?: ProductDto[];
    attributesValueByProduct?: AttributeValueByProduct[];
    productInfosByProductId: SafeDictionary<ProductInfo>;
    attributeInfosByProductId: SafeDictionary<AttributeValueModel>;
    attributeTypeLabel?: string;
    isLoading: boolean;
    pricingLoaded?: boolean;
    inventoryLoaded?: boolean;
};

type HandlerType = Handler<BatchGetProductsByCustomVariantApiParameter & HasOnSuccess, Props>;
export const DispatchLoadProductByVariant: HandlerType = (props: any) => {
    props.dispatch({
        type: "Custom/loadBundledProductsByCustomVariant",
        parameter: props.parameter,
    });
};

export const LoadBundledProductsByCustomVariant: HandlerType = async props => {
    const parameter = props.parameter;
    if (!parameter) {
        return false;
    }

    props.apiResult = await batchGetProductsByCustomVariant(props.parameter);
    props.products = props.apiResult?.products;
    props.attributesValueByProduct = props.apiResult?.attributesValueByProduct;
    props.attributeTypeLabel = props.apiResult?.attributeTypeLabel;

    if (!props.apiResult) {
        return false;
    }
};

export const MapProducts: HandlerType = props => {
    props.productInfosByProductId = {};
    props.attributeInfosByProductId = {};
    props.products?.forEach(o => {
        props.productInfosByProductId[o.id] = {
            productId: o.id,
            qtyOrdered: Math.max(o.qtyOrdered, 1),
            unitOfMeasure: o.unitOfMeasure,
            productDetailPath: o.productDetailUrl,
        };
        const attributeValue = props.attributesValueByProduct?.find((e: any) => e.productId === o.id);
        if (attributeValue) {
            props.attributeInfosByProductId[o.id] = {
                id: attributeValue.id,
                value: attributeValue.value,
                sortOrder: attributeValue.sortOrder,
                valueDisplay: attributeValue.value,
            };
        }
    });
};

export const LoadRealTimePrices: HandlerType = async props => {
    const { products, productInfosByProductId } = props;
    if (!products?.length) {
        props.pricingLoaded = true;
        return;
    }

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: products!.map(o => ({
                productId: o.id,
                unitOfMeasure: o.unitOfMeasure,
                qtyOrdered: o.qtyOrdered,
            })),
            onComplete: realTimePricingProps => {
                if (realTimePricingProps?.apiResult) {
                    realTimePricingProps.apiResult.realTimePricingResults?.forEach(pricing => {
                        for (const productListLineId in productInfosByProductId) {
                            const productInfo = productInfosByProductId[productListLineId]!;
                            if (
                                productInfo.productId === pricing.productId &&
                                productInfo.unitOfMeasure === pricing.unitOfMeasure
                            ) {
                                productInfo.pricing = pricing;
                            }
                        }
                    });
                } else if (realTimePricingProps.error) {
                    for (const productListLineId in productInfosByProductId) {
                        productInfosByProductId[productListLineId]!.failedToLoadPricing = true;
                    }
                }
                props.pricingLoaded = true;
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

export const LoadRealTimeInventory: HandlerType = props => {
    const { products, productInfosByProductId } = props;
    if (!products?.length) {
        props.inventoryLoaded = true;
        return;
    }

    const productListLineIds = Object.keys(productInfosByProductId);
    props.dispatch(
        loadRealTimeInventory({
            productIds: productListLineIds.map(o => productInfosByProductId[o]!.productId),
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps?.error) {
                    productListLineIds.forEach(o => {
                        const productInfo = productInfosByProductId[o]!;
                        if (productInfo) {
                            productInfo.failedToLoadInventory = true;
                        }
                    });
                } else {
                    realTimeInventoryProps?.apiResult?.realTimeInventoryResults?.forEach(inventory => {
                        productListLineIds.forEach(o => {
                            const productInfo = productInfosByProductId[o]!;
                            if (productInfo.productId === inventory.productId) {
                                productInfo.inventory = inventory;
                            }
                        });
                    });
                }

                props.inventoryLoaded = true;
            },
        }),
    );
};
export const WaitForData: HandlerType = async (props: any) => {
    const checkData = () => {
        return !!props.pricingLoaded && !!props.inventoryLoaded;
    };

    await waitFor(checkData);
};
export const DispatchCompleteLoadProductByCustomVariant: HandlerType = (props: any) => {
    props.dispatch({
        type: "Custom/DispatchCompleteLoadProductByCustomVariant",
        products: props.products,
        productInfosByProductId: props.productInfosByProductId,
        attributeInfosByProductId: props.attributeInfosByProductId,
        attributeTypeLabel: props.attributeTypeLabel,
    });
};

export const chain = [
    DispatchLoadProductByVariant,
    LoadBundledProductsByCustomVariant,
    MapProducts,
    LoadRealTimePrices,
    LoadRealTimeInventory,
    WaitForData,
    DispatchCompleteLoadProductByCustomVariant,
];

const loadBundledProductsByCustomVariant = createHandlerChainRunner(chain, "loadBundledProductsByCustomVariant");
export default loadBundledProductsByCustomVariant;
